import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../../components/lds/LdsTable";
import { displayFor, resolvePath } from "../definition";
import { columnHeader, groupLabel, measureLabel, sampleCell } from "../labels";
import type { Catalog, ReportSpec } from "../types";
import { ListCell } from "./ResultTable";
import { fitWidth } from "./styles";

type Props = { catalog: Catalog; definition: ReportSpec };

/**
 * What the report will look like, built from the catalog alone: column headers and three example rows with
 * neutral placeholders. No data is fetched while composing.
 */
export default function LayoutPreview({ catalog, definition }: Props): ReactElement {
  const { t } = useTranslation();
  const summary = definition.summary;
  const columns = summary ? [
    ...summary.groups.map((group, index) => ({
      column: { path: `g${index}` },
      header: groupLabel(t, catalog, definition.source, group),
      type: group.bucket ? "NUMBER" : resolvePath(catalog, definition.source, group.path)?.field?.type ?? "NUMBER",
      display: "VALUE",
      many: false,
    } as const)),
    ...summary.measures.map((measure, index) => ({
      column: { path: `m${index}` },
      header: measureLabel(t, catalog, definition.source, measure),
      type: "NUMBER",
      display: "COUNT",
      many: false,
    } as const)),
  ] : definition.columns.map((column) => {
    const resolved = resolvePath(catalog, definition.source, column.path);
    return {
      column,
      header: columnHeader(t, catalog, definition.source, column),
      type: resolved?.field?.type ?? "NUMBER",
      display: resolved ? displayFor(resolved) : "VALUE",
      many: Boolean(resolved?.many),
    } as const;
  });

  return (
    <LdsTableCard className={fitWidth} aria-label={String(t("reports.builder.layout", { defaultValue: "Layout of the report" }))}>
      <div className="flex items-center justify-between gap-3 px-5 pt-4 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        <span>{t("reports.builder.layout", { defaultValue: "Layout of the report" })}</span>
        <span className="normal-case tracking-normal">
          {t("reports.builder.layout_hint", { defaultValue: "Example rows, not real data" })}
        </span>
      </div>
      <LdsTableScroll className="p-3">
        <LdsTable>
          <LdsTableHead>
            <LdsTableRow>
              {columns.map(({ column, header, many }) => (
                <LdsTableHeadCell key={column.path}>
                  <span className="inline-flex items-center gap-1">
                    {header}
                    {many && (
                      <span
                        className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-300"
                        title={String(t("reports.builder.many_hint", { defaultValue: "Several per row: combined in one cell" }))}
                      >
                        {t("reports.builder.combined", { defaultValue: "combined" })}
                      </span>
                    )}
                  </span>
                </LdsTableHeadCell>
              ))}
            </LdsTableRow>
          </LdsTableHead>
          <LdsTableBody>
            {[0, 1, 2].map((row) => (
              <LdsTableRow key={row}>
                {columns.map(({ column, type, display }) => (
                  <LdsTableCell key={column.path} className="align-top italic text-slate-400 dark:text-slate-500">
                    {display === "LIST"
                      ? <ListCell items={sampleCell(t, type, display, row).split("\n")} />
                      : sampleCell(t, type, display, row)}
                  </LdsTableCell>
                ))}
              </LdsTableRow>
            ))}
          </LdsTableBody>
        </LdsTable>
      </LdsTableScroll>
    </LdsTableCard>
  );
}
